/**
 * Filtros guardados del Libro de IVA (Entrega 4.2).
 *
 * Persistencia en localStorage por empresa. Independientes por usuario porque
 * el localStorage es del navegador (alineado con cómo se gestiona ya el
 * registro de "contabilizadas" en `contabilizado.ts`).
 *
 * Estructura serializada de `columnFilters` que usa VatBook.tsx:
 *   { [colKey: string]: string[] }   // solo se incluyen las columnas con filtro
 *
 * Junto con la opción `onlyRecurrent` y un nombre descriptivo.
 */

export interface SavedFilterPayload {
  /** columnFilters como objeto: { col: [valores...] }. Sólo columnas con filtros. */
  columns: Record<string, string[]>;
  /** Toggle "solo recurrentes". */
  onlyRecurrent: boolean;
  /** Toggle "solo facturas con IRPF". */
  onlyWithIrpf?: boolean;
  /** Toggle "solo trimestre actual". */
  onlyCurrentQuarter?: boolean;
}

export interface SavedFilter extends SavedFilterPayload {
  id: string;
  name: string;
  builtIn?: boolean;
  /** Si está presente, identifica un preset (no editable). */
  presetKey?: string;
}

const KEY_PREFIX = "fyp:vat-book-filters:";

const storageKey = (companyId: string) => `${KEY_PREFIX}${companyId || "_default"}`;

const safeRead = (companyId: string): SavedFilter[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(storageKey(companyId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x) => x && typeof x === "object" && typeof x.id === "string" && typeof x.name === "string");
  } catch {
    return [];
  }
};

const safeWrite = (companyId: string, list: SavedFilter[]): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(companyId), JSON.stringify(list));
  } catch {
    /* ignore quota errors */
  }
};

export const listSavedFilters = (companyId: string): SavedFilter[] => safeRead(companyId);

export const saveFilter = (
  companyId: string,
  name: string,
  payload: SavedFilterPayload,
): SavedFilter => {
  const list = safeRead(companyId);
  const filter: SavedFilter = {
    id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    name: name.trim() || "Sin nombre",
    columns: payload.columns,
    onlyRecurrent: payload.onlyRecurrent,
  };
  list.push(filter);
  safeWrite(companyId, list);
  return filter;
};

export const renameFilter = (companyId: string, id: string, newName: string): void => {
  const list = safeRead(companyId).map((f) =>
    f.id === id ? { ...f, name: newName.trim() || f.name } : f,
  );
  safeWrite(companyId, list);
};

export const deleteFilter = (companyId: string, id: string): void => {
  const list = safeRead(companyId).filter((f) => f.id !== id);
  safeWrite(companyId, list);
};

/**
 * Presets predefinidos. Se computan dinámicamente porque dependen del trimestre
 * actual o del conjunto de NIFs recurrentes (que se inyecta al aplicarlos).
 */
export const buildPresets = (opts: {
  currentQuarter: { start: string; end: string }; // yyyy-mm-dd
}): SavedFilter[] => [
  {
    id: "preset:con-irpf",
    presetKey: "con-irpf",
    builtIn: true,
    name: "Con IRPF",
    columns: {},
    onlyRecurrent: false,
    onlyWithIrpf: true,
  },
  {
    id: "preset:trimestre",
    presetKey: "trimestre",
    builtIn: true,
    name: `Trimestre actual (${opts.currentQuarter.start.slice(0, 7)} → ${opts.currentQuarter.end.slice(0, 7)})`,
    columns: {},
    onlyRecurrent: false,
    onlyCurrentQuarter: true,
  },
  {
    id: "preset:recurrentes",
    presetKey: "recurrentes",
    builtIn: true,
    name: "Recurrentes",
    columns: {},
    onlyRecurrent: true,
  },
];

/** Devuelve {start, end} ISO del trimestre natural en el que cae `today`. */
export const currentQuarterRange = (today: Date = new Date()): { start: string; end: string } => {
  const y = today.getFullYear();
  const q = Math.floor(today.getMonth() / 3); // 0..3
  const startMonth = q * 3;
  const start = new Date(y, startMonth, 1);
  const end = new Date(y, startMonth + 3, 0);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: fmt(start), end: fmt(end) };
};
